import { useEffect, useState } from 'react';
import type { Editor, EditorEvents } from '@tiptap/react';
import { PARAGRAPH_INITIAL_LEVEL } from '../../../grid/reduxStore/defaultTextDefaultValue.ts';
import { DATA_TABLE_STYLE } from '../../../grid/reduxStore/tableDefaultStyleValues.ts';

type SelectedLevel = string | undefined;

export const useSpotStyleChecker = (editor: Editor | null) => {
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevel>(PARAGRAPH_INITIAL_LEVEL);
  const [isSpotStyleActive, setIsSpotStyleActive] = useState<boolean>(false);

  useEffect(() => {
    const updateSelectedLevel = ({ editor }: EditorEvents['transaction']) => {
      const { from, to } = editor.state.selection;
      const headingsInSelection: string[] = [];
      let styleName = false;
      let hasMarks = false;

      const processNodeForLevel = (node) => {
        if (node?.attrs?.level > 0) {
          headingsInSelection.push(`h${node.attrs.level}`);
        } else if (node.type?.name === 'paragraph' && !node.attrs?.[DATA_TABLE_STYLE]) {
          headingsInSelection.push(PARAGRAPH_INITIAL_LEVEL);
        } else if (node.attrs?.[DATA_TABLE_STYLE]) {
          headingsInSelection.push(node.attrs?.[DATA_TABLE_STYLE]);
        }

        if (node.marks?.length > 0) {
          hasMarks = true;
        }
      };

      if (editor.state.selection.empty) {
        processNodeForLevel(editor.state.selection.$from.parent);
      } else {
        editor.state.doc.nodesBetween(from, to, processNodeForLevel);
      }
      if (hasMarks) {
        styleName = true;
      }

      setIsSpotStyleActive(styleName);

      const headingsSet = new Set(headingsInSelection);
      if (headingsSet.size === 1) {
        setSelectedLevel(Array.from(headingsSet)[0]);
      } else {
        setSelectedLevel(undefined);
      }
    };

    editor?.on('transaction', updateSelectedLevel);
    return () => {
      editor?.off('transaction', updateSelectedLevel);
    };
  }, [editor]);

  return { isSpotStyleActive, selectedLevel, setSelectedLevel };
};
